import { SimpleReadOnlyEvent } from '@passionware/simple-event';
import { V1Action } from 'v4/migration/actionAsEventMiddleware';

/**
 * Symmetric bridge between v5 IO services and the Redux store.
 *
 * - `write.emit` → `store.dispatch` (wired in `.connected.ts`)
 * - `read` ← middleware fan-out (`myV1ActionEvent`), just before the reducers
 *   run: a listener reads the action, never Redux state
 *
 * Both sides use {@link V1Action}. Extend that union when a service needs a
 * new action shape — do not widen `write` to a loose `{ type: string }`.
 *
 * Do not make `write` and `read` the same event: write must go through the
 * store so reducers run once; the middleware notifies `read` listeners.
 */
export type ReduxProxy = {
  write: {
    emit: (action: V1Action) => void;
  };
  read: SimpleReadOnlyEvent<V1Action>;
};

export function createReduxProxy(config: {
  dispatch: (action: V1Action) => void;
  read: SimpleReadOnlyEvent<V1Action>;
}): ReduxProxy {
  return {
    write: {
      emit: action => config.dispatch(action)
    },
    read: config.read
  };
}
